import React, { createContext, useContext, useState, useEffect } from 'react';
import { OnboardingStatus, User, UserPersona } from '@demind-inc/core';
import { isEmpty } from 'lodash';

import { OnboardingStep, OnboardingStepKey } from './types';
import { trackEventMixpanel } from '../../utils';
import { useAuthContext } from './AuthProvider';
import { useUpdateOnboardingStatus } from '../mutations';
import { useSubscriptionContext } from './SubscriptionProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { ONBOARDING_PATH, Path } from '../../routing';
import {
  getFirstOnboardingStepsAfterPaywall,
  saveLastOnboardingStepAfterPaywall,
  clearLastOnboardingStepAfterPaywall,
} from './onboarding.constants';

interface OnboardingContextType {
  currentOnboardingStep: OnboardingStep;
  surveyData: UserPersona;
  updateInfoAfterSignUp: (targetUser?: User) => Promise<void>;
  findNextStep: () => OnboardingStep | null;
  goToSpecificStep: (stepKey: OnboardingStepKey) => void;
  goToNextStep: (showIfData?: any) => void;
  goToPreviousStep: () => void;
  finishOnboardingBeforePaywall: () => Promise<void>;
  finishOnboardingAfterPaywall: () => Promise<void>;
  updateSurveyData: (data: Partial<UserPersona>) => void;
  updateOnboardingStatus: (status: Partial<OnboardingStatus>) => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

interface OnboardingProviderProps {
  children: React.ReactNode;
  onboardingSteps: OnboardingStep[];
  initialStep?: OnboardingStepKey;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
  onboardingSteps,
  initialStep,
}) => {
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState<OnboardingStep>(() => {
    if (initialStep) {
      const step = onboardingSteps.find((s) => s.key === initialStep);
      return step || onboardingSteps[0];
    }
    return onboardingSteps[0];
  });
  const [surveyData, setSurveyData] = useState<UserPersona>({} as UserPersona);

  const { updateOnboardingStatus: updateOnboardingStatusApi } = useUpdateOnboardingStatus();
  const { user, updateUser } = useAuthContext();
  const { hasAccessToApp } = useSubscriptionContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Update currentOnboardingStep based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    const matchingStep = onboardingSteps.find((step) => ONBOARDING_PATH[step.key] === currentPath);
    if (matchingStep) {
      setCurrentOnboardingStep(matchingStep);
      if (matchingStep.afterPaywall) {
        saveLastOnboardingStepAfterPaywall(matchingStep.key);
      }
    }
  }, [location.pathname]);

  const updateSurveyData = (data: Partial<UserPersona>) => {
    const nextPersona = { ...surveyData, ...data };
    setSurveyData(nextPersona);

    if (user?.userId) {
      void updateUser({
        persona: {
          ...(user.persona || {}),
          ...nextPersona,
        },
      });
    }
  };

  const updateInfoAfterSignUp = async (targetUser?: User) => {
    if (!targetUser?.userId) {
      return;
    }
    await updateUser(
      {
        onboarding: {
          ...(targetUser.onboarding || {}),
          beforePaywall: 'answered',
        },
      },
      targetUser.userId
    );
  };

  const updateOnboardingStatus = async (status: Partial<OnboardingStatus>) => {
    if (!user?.userId) {
      return;
    }
    await updateOnboardingStatusApi({
      userId: user.userId,
      newStatus: status,
    });
  };

  const findNextStep = () => {
    const currentIndex = onboardingSteps.findIndex(
      (step) => step.key === currentOnboardingStep.key
    );
    if (currentIndex < onboardingSteps.length - 1) {
      const next = onboardingSteps.find((step) => step.order === currentOnboardingStep.order + 1);
      if (next) {
        return next;
      }
    }
    return null;
  };

  const goToSpecificStep = (stepKey: OnboardingStepKey) => {
    const step = onboardingSteps.find((step) => step.key === stepKey);
    if (step) {
      setCurrentOnboardingStep(step);
      navigate(ONBOARDING_PATH[step.key]);
    }
  };

  const goToNextStep = (showIfData?: any) => {
    const currentIndex = onboardingSteps.findIndex(
      (step) => step.key === currentOnboardingStep.key
    );
    if (currentIndex >= onboardingSteps.length - 1) {
      void finishOnboardingAfterPaywall();
      return;
    }

    const next = onboardingSteps.find((step) => step.order === currentOnboardingStep.order + 1);
    if (!next) {
      void finishOnboardingAfterPaywall();
      return;
    }

    if (next.showIf && !next.showIf(showIfData)) {
      // Find the next eligible step instead of recursively calling
      const nextEligibleStep = onboardingSteps.find(
        (step) => step.order > next.order && (!step.showIf || step.showIf(showIfData))
      );

      if (nextEligibleStep) {
        if (nextEligibleStep.disableToGoBack) {
          navigate(ONBOARDING_PATH[nextEligibleStep.key], { replace: true });
        } else {
          navigate(ONBOARDING_PATH[nextEligibleStep.key]);
        }
        trackEventMixpanel('onboarding_step_next', {
          step: nextEligibleStep.key,
        });
      } else {
        void finishOnboardingAfterPaywall();
      }
      return;
    }

    if (next.disableToGoBack) {
      navigate(ONBOARDING_PATH[next.key], { replace: true });
    } else {
      navigate(ONBOARDING_PATH[next.key]);
    }
    trackEventMixpanel('onboarding_step_next', {
      step: next.key,
    });
  };

  const finishOnboardingBeforePaywall = async () => {
    await updateOnboardingStatus({
      beforePaywall: 'answered',
    });

    const hasFinishedAfterPaywall = !!user?.onboarding?.afterPaywall;
    if (hasAccessToApp) {
      if (hasFinishedAfterPaywall) {
        navigate(Path.DASHBOARD, { replace: true });
      } else {
        const nextAfterPaywallStep = getFirstOnboardingStepsAfterPaywall();
        if (nextAfterPaywallStep) {
          navigate(ONBOARDING_PATH[nextAfterPaywallStep], { replace: true });
        } else {
          navigate(Path.DASHBOARD, { replace: true });
        }
      }
    } else {
      navigate(Path.PAYWALL, { replace: true });
    }
  };

  const finishOnboardingAfterPaywall = async () => {
    await updateOnboardingStatus({
      afterPaywall: 'answered',
      initialSync: 'answered',
    });
    clearLastOnboardingStepAfterPaywall();
    navigate(Path.DASHBOARD, { replace: true });
    trackEventMixpanel('onboarding_finish', {
      step: 'dashboard',
    });
  };

  const goToPreviousStep = () => {
    if (window.history?.length) {
      // Redirect to previous page if there is a history
      navigate(-1);
      return;
    }
    const currentIndex = onboardingSteps.findIndex(
      (step) => step.key === currentOnboardingStep.key
    );

    if (currentIndex > 0) {
      const previous = onboardingSteps.find(
        (step) => step.order === currentOnboardingStep.order - 1
      );
      if (previous) {
        trackEventMixpanel('onboarding_step_previous', {
          step: previous.key,
        });
        navigate(ONBOARDING_PATH[previous.key]);
      }
      return;
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        currentOnboardingStep,
        surveyData,
        findNextStep,
        updateInfoAfterSignUp,
        goToSpecificStep,
        goToNextStep,
        goToPreviousStep,
        finishOnboardingBeforePaywall,
        finishOnboardingAfterPaywall,
        updateSurveyData,
        updateOnboardingStatus,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
